import React, { useState, useEffect, useContext } from "react";
import { useDispatch, ReactReduxContext, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import caseStates from "@_configs/caseStates.config";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/CheckCircle";
import {
  formatDatetime,
  getCurrencySign,
  validateEmail,
  isNull,
  shortnString,
  catchError,
  getUserDataFromJwt,
} from "@_utils/helpers";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LangIcon from "@mui/icons-material/Language";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import ActivateCasePopup from "@_components/popups/ActivateCasePopup/ActivateCasePopup";
import StripeInfoPopup from "@_components/popups/StripeInfoPopup/StripeInfoPopup";
import CancelCasePopup from "@_components/popups/CancelCasePopup/CancelCasePopup";
import InfoPaymentPopup from "@_components/popups/InfoPaymentPopup/InfoPaymentPopup";
import InfoLicensePopup from "@_components/popups/InfoLicensePopup/InfoLicensePopup";
import ContactInfoPopup from "@_components/popups/ContactInfoPopup/ContactInfoPopup";
import SelectEmailFlowPopup from "@_components/popups/SelectEmailFlowPopup/SelectEmailFlowPopup";
import SelectTranslationPopup from "@_components/popups/SelectTranslationPopup/SelectTranslationPopup";
import ReminderPopup from "@_components/popups/ReminderPopup/ReminderPopup";
import RenderPopup from "@_components/popups/RenderPopup/RenderPopup";
import { authService } from "@_services/auth.service";

import { templateService } from "@_services/template.service";
import ClaimPopup from "@_components/popups/ClaimPopup/ClaimPopup";
import styles from "./OverviewSection.module.css";
import NextIcon from "@mui/icons-material/NavigateNextOutlined";
import FindingsPopup from "@_components/popups/FindingsPopup/FindingsPopup";
import AddFindingPopup from "@_components/popups/AddFindingPopup/AddFindingPopup";
import { languageActions } from "@_actions/language.actions";
import { emailSetupActions } from "@_actions/email_setup.actions";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import TemplatePopup from "@_components/popups/TemplatePopup/TemplatePopup";
import {
  AddCircleOutline,
  EmailOutlined,
  LaunchOutlined,
} from "@mui/icons-material";
import { caseConstants } from "@_constants/case.constants";
import { emailConstants } from "@_constants/email_flows.constants";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import { Chip, ListSubheader } from "@mui/material";
import InfoPopup from "@_components/popups/InfoPopup/InfoPopup";
import { invoiceTemplateConstants } from "@_constants/invoiceTemplate.constants";
import AddCaseTagPopup from "@_components/popups/AddCaseTagPopup/AddCaseTagPopup";
import { customerActions } from "@_actions/customer.actions";
import { caseTemplateActions } from "@_actions/caseTemplate.actions";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";

function OverviewSection(props) {
  const dispatch = useDispatch();
  const { data } = props;

  const emailSetupReducer = useSelector((state) => state.emailSetupReducer);
  const languageReducer = useSelector((state) => state.languageReducer);
  const caseReducer = useSelector((state) => state.caseReducer);
  const caseActivityReducer = useSelector((state) => {
    return state.caseActivityReducer;
  });

  const [openActivateCasePopup, setOpenActivateCasePopup] = useState(false);
  const [openStripeInfoPopup, setOpenStripeInfoPopup] = useState(false);

  const [openCancelCasePopup, setOpenCancelCasePopup] = useState(false);
  const [templatePopupOpen, setTemplatePopupOpen] = useState(false);
  const [openResetCasePopup, setOpenResetCasePopup] = useState(false);
  const [openInfoPaymentPopup, setOpenInfoPaymentPopup] = useState(false);
  const [openInfoLicensePopup, setOpenInfoLicensePopup] = useState(false);
  const [openClaimPopup, setOpenClaimPopup] = useState(false);
  const [openContactInfoPopup, setOpenContactInfoPopup] = useState(false);
  const [openReminderPopup, setOpenReminderPopup] = useState(false);
  const [openAddCaseTagPopup, setOpenAddCaseTagPopup] = useState(false);
  const [reminderData, setReminderData] = useState(
    caseReducer.case[props.data.id],
  );

  const [openFindingsPopup, setOpenFindingsPopup] = useState(false);
  const [openAddFindingPopup, setOpenAddFindingPopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { store } = useContext(ReactReduxContext);
  const { snackbarProps, showSnackbarSuccess, showSnackbarError } =
    useSnackbar();

  const [openEmailFlowPopup, setOpenEmailFlowPopup] = useState(false);
  const [openTranslationPopup, setOpenTranslationPopup] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailData, setEmailData] = useState({});
  const [openRenderPopup, setOpenRenderPopup] = useState(false);
  const [isTaxable, setIsTaxable] = useState(false);
  const [validTax, setValidTax] = useState(false);
  const [selectedTax, setSelectedTax] = useState("");
  const [isConnected, setIsConnected] = useState();
  const [stripeUrl, setStripeUrl] = useState();
  const [flow, setFlow] = useState();
  const [displayedContactData, setDisplayedContactData] = useState("");
  const [openPreviewMenu, setOpenPreviewMenu] = useState(false);
  const [openInfoPopup, setOpenInfoPopup] = useState(false);
  const [infoPopupText, setInfoPopupText] = useState("");
  const [InfoPopupTitle, setInfoPopupTitle] = useState("");

  const validTranslation = languageReducer.translations.default_translation_id;
  const emailSetup = emailSetupReducer.email_setup.active_email_setup;
  const jwtData = getUserDataFromJwt();

  /**
   * Keeps local `flow` state aligned with the case’s `email_flow` from the server.
   *
   * @triggers
   * When `data.email_flow` or `flow` changes.
   *
   * @user_flow
   * 1. Case data updates from Redux/parent.
   * 2. If `email_flow` has an id, copy it into `flow`; otherwise clear `flow`.
   */
  useEffect(() => {
    data.email_flow && data.email_flow.id
      ? setFlow(data.email_flow)
      : setFlow();
  }, [data.email_flow, flow]); // ← flow here triggers re-run after setFlow()

  //condition can be refactored to consume less lines and be more readable,
  const validContact =
    data.opponent_contact_details &&
    data.opponent_contact_details.email &&
    !isNull(data.opponent_contact_details.email) &&
    validateEmail(data.opponent_contact_details.email);

  const validClaim =
    data.charge_details &&
    data.charge_details.total_net_amount_findings > 0 &&
    ((data.claim_details.settlement_offer_date &&
      data.claim_details.settlement_offer_percentage) ||
      (!data.claim_details.settlement_offer_date &&
        !data.claim_details.settlement_offer_percentage)) &&
    data.charge_details &&
    data.charge_details.total_net_amount_findings > 0 &&
    data.country &&
    !isNull(data.country) &&
    !data.findings.some((item) => !item.price || item.price <= 0) &&
    (!data.claim_details.additional_fees ||
      !data.claim_details.additional_fees.some(
        (item) => !item.percentage || !item.name || item.price <= 0,
      )) &&
    data.charge_details.currency &&
    !isNull(data.country) &&
    data.charge_details.indirect_tax_type &&
    (isTaxable ? validTax : !isTaxable);

  const activable = validContact && validClaim && flow && validTranslation;
  const currState = store.getState().casesReducer.status.CASE_ADD_FINDING;

  /**
   * Loads email setup and case templates when the component first mounts.
   *
   * This runs once to fetch the SMTP configuration and available templates
   * so they're ready for use in mail menus and template pickers.
   */
  useEffect(() => {
    dispatch(emailSetupActions.getEmailSetup());
    dispatch(caseTemplateActions.getCaseTemplates());
  }, [dispatch]);

  /**
   * Reacts when the user applies a template to the case.
   *
   * If successful: closes the popup, refetches the case data, and refreshes the cases list.
   * If error: shows an error message to the user.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_APPLY_TEMPLATE === "success") {
      setTemplatePopupOpen(false);
      dispatch(caseActions.clearStateMessage("CASE_APPLY_TEMPLATE"));
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data));
      dispatch(casesActions.getCases());
    } else if (catchError(caseReducer.status.CASE_APPLY_TEMPLATE)) {
      showSnackbarError(caseReducer.status.CASE_APPLY_TEMPLATE);
      dispatch(caseActions.clearStateMessage("CASE_APPLY_TEMPLATE"));
    }
  }, [
    caseReducer.status.CASE_APPLY_TEMPLATE,
    data,
    dispatch,
    showSnackbarError,
  ]);

  /**
  Sets the first finding (image) as the default preview when findings change.
   * @triggers
   * When findings change.
  */
  useEffect(() => {
    setSelectedImage(data.findings && data.findings[0]);
  }, [data.findings]);

  /**
   * Fetches email templates for the currently selected mail flow.
   *
   * If a flow is selected, load its templates. If not, clear the template list.
   * Adds a "New Email" option if the case is not in standby mode.
   * @triggers
   * When flow id or case state changes.
   * issue 1: The error is not handled — it's just logged as a TODO comment.
   * Silent failure — If the API call fails, the template list won't update
   * can also be refactored to use actions and reducers to handle API call and state, instead of local state and direct service call in the component.
   */
  useEffect(() => {
    flow && flow.id
      ? templateService.getTemplates(flow.id).then(
          (res) => {
            if (!res) res = [];
            if (data.state !== caseConstants.CASE_STATE_STANDBY) {
              res.push({
                id: emailConstants.NEW_MAIL,
                name: "New Email",
              });
            }
            setTemplates(res);
          },
          (error) => {
            showSnackbarError(error);
          },
        )
      : setTemplates([]);
  }, [flow, data.state, showSnackbarError]);

  /**
   * Loads the translations for settlement-portal language rules.
   *
   * Used with `validTranslation` / activation: user must have a default translation configured
   * before activating a case (`activable` depends on `validTranslation`).
   *
   * @triggers
   * Loads the available translations when the component mounts.
   * improvements:
   * Move it with other 2 API calls on mount
   */
  useEffect(() => {
    dispatch(languageActions.getTranslations());
  }, [dispatch]);

  const [openMenu, setOpenMenu] = useState(null);
  const handleCloseMenu = (event) => {
    event.stopPropagation();
    setOpenMenu(null);
  };

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    flow
      ? setOpenMenu(event.currentTarget)
      : handleOpenInfoPopup(
          "No mail flow assigned to the case.",
          "Please select a mail flow to send mails",
        );
  };

  const handleOpenPreviewMenu = (event) => {
    event.stopPropagation();
    flow
      ? setOpenPreviewMenu(event.currentTarget)
      : handleOpenInfoPopup(
          "No mail flow assigned to the case.",
          "Please select a mail flow to preview mails",
        );
  };

  const handleOpenInfoPopup = (title, text) => {
    setInfoPopupTitle(title);
    setInfoPopupText(text);
    setOpenInfoPopup(true);
  };

  const handleClosePreviewMenu = (event) => {
    event.stopPropagation();
    setOpenPreviewMenu(false);
  };

  const handleOpenEmailFlowPopup = (event) => {
    event.stopPropagation();
    setOpenEmailFlowPopup(true);
  };

  const handleOpenTemplatePopup = (event, template_id, name, type) => {
    event.stopPropagation();
    handleCloseMenu(event);
    handleClosePreviewMenu(event);
    if (template_id === emailConstants.NEW_MAIL) {
      setEmailData({
        content: "",
        name: emailConstants.NEW_MAIL,
        subject: "",
        opponentEmail: data.opponent_contact_details?.email,
      });
      setOpenRenderPopup(true);
    } else {
      // could be refactored to handle this logic using the actions and call it in the popup instead?
      templateService.getCaseTemplate(data.id, template_id).then(
        (template) => {
          //it doesn't matter because we need the send to address (opponent address) not the writtendown address
          let to = data.opponent_contact_details?.email;
          if (
            type === emailConstants.TEMPLATE_TYPE_LETTER &&
            data.opponent_contact_details
          ) {
            to =
              (data.opponent_contact_details.name
                ? data.opponent_contact_details.name + ", "
                : "") +
              (data.opponent_contact_details.company
                ? data.opponent_contact_details.company + ", "
                : "") +
              (data.opponent_contact_details.address_line_1
                ? data.opponent_contact_details.address_line_1 + ", "
                : "") +
              (data.opponent_contact_details.address_line_2
                ? data.opponent_contact_details.address_line_2 + ", "
                : "") +
              (data.opponent_contact_details.postal_code
                ? data.opponent_contact_details.postal_code + " "
                : "") +
              (data.opponent_contact_details.city
                ? data.opponent_contact_details.city + ", "
                : "") +
              (data.opponent_contact_details.country
                ? data.opponent_contact_details.country
                : "");
          }
          setEmailData({
            content: template.content,
            name: name,
            subject: template.subject,
            opponentEmail: to,
            type: type,
            template_id: template_id,
          });
          setOpenRenderPopup(true);
        },
        (error) => {
          showSnackbarError(error);
        },
      );
    }
  };

  /**
   * 1. User opens "Add Finding" popup (from overview or cases list).
   * 2. User selects image, uploads, confirms.
   * 3. Finding added action completes and becomes "success".
   * 4. This effect detects "success".
   * 5. Close add popup, set snackbar message.
   * 6. Show success snackbar: "Finding added!".
   * @triggers
   * When the finding addition status changes to "success".
   */
  useEffect(() => {
    if (currState === "success") {
      setOpenAddFindingPopup(false);
      showSnackbarSuccess("Finding added!");
    }

    /* Better approach: watch only `currState` so the effect
       runs once when it flips to success. */
  }, [currState, showSnackbarSuccess]);

  /**
   * Shows success or error message after user saves claim details or additional fees.
   *
   * @triggers
   * when API response comes back from saving claim or fees (When user clicks "Save" in ClaimPopup)
   *
   * Why the additional fees are treated differently, isn't part of the claim details? why there ere 2 API calls?
   */
  useEffect(() => {
    if (caseReducer.status.CASE_UPDATE_CLAIM_DETAILS === "success") {
      showSnackbarSuccess("Success");
      /* Doesn't refresh the case after successful save, the case data isn't refetched. 
        Changes might not reflect in the UI. And no status clearing for success path of CASE_ADD_ADDITIONAL_FEES*/
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_CLAIM_DETAILS"));
    } else if (
      catchError(caseReducer.status.CASE_UPDATE_CLAIM_DETAILS) ||
      catchError(caseReducer.status.CASE_ADD_ADDITIONAL_FEES)
    ) {
      // On CASE_ADD_ADDITIONAL_FEES error, it still clears CASE_UPDATE_CLAIM_DETAILS
      showSnackbarError(
        catchError(caseReducer.status.CASE_UPDATE_CLAIM_DETAILS)
          ? caseReducer.status.CASE_UPDATE_CLAIM_DETAILS
          : caseReducer.status.CASE_ADD_ADDITIONAL_FEES,
      );
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_CLAIM_DETAILS"));
    }
    /* Improvements: Handles two different operations in one effect 
    - Separate effects — Each operation has its own effect
    - Correct status clearing — Clears the right status
    - Case refresh — Data updates after save
    - Clear error messages */
  }, [
    caseReducer.status.CASE_UPDATE_CLAIM_DETAILS,
    caseReducer.status.CASE_ADD_ADDITIONAL_FEES,
    dispatch,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  const langSelectionCLicked = () => {
    if (validTranslation) {
      setOpenTranslationPopup(true);
    } else {
      alert(
        "You must create and activate a translation under Admin > Settlement Portal > Translations before activating this case.",
      );
    }
  };

  const openEvidancePopup = () => {
    if (data.findings.length > 0) setOpenFindingsPopup(data.findings);
    else setOpenAddFindingPopup(true);
  };

  const handleCaseActivation = () => {
    authService.getStripe().then(
      (results) => {
        if (results.status === "awaiting_onboarding") {
          setStripeUrl(results.onboarding_url);
          setIsConnected(false);
          setOpenStripeInfoPopup(true);
        } else {
          setIsConnected(true);
          setOpenActivateCasePopup(true);
          // either connected or diffrent status response with 200
        }
      },
      // issue 1: The error is not handled — it's just logged as a TODO comment.
      (error) => {
        showSnackbarError(error);
      },
    );
  };

  const handleAutoLogin = () => {
    let url = data.portal_link.match(/href=['"]([^'"]+)['"]/)[1];
    url = url + `&user_id=${jwtData.user_id}`;
    window.open(url, "_blank");
  };

  /*
   * Checks if indirect tax (VAT/Sales Tax/GST) applies and if percentage is valid.
   * Used to determine if "ACTIVATE" button should be enabled.
   * @triggers
   * When either claim details change.
   */
  useEffect(() => {
    /* Hard to follow what validTax means when isTaxable is false, 
      better approach using variables, Clearer logic, Easier to debug
      const taxType = data.claim_details.indirect_tax_type;
      const taxPercentage = data.claim_details.indirect_tax_percentage;
      const isTaxable = taxType && 
      taxType !== caseConstants.CASE_TAX_TYPE_NO_INDIRECT_TAX && 
      taxType !== caseConstants.CASE_TAX_TYPE_REVERSE_CHARGE; */
    if (
      data.claim_details.indirect_tax_type &&
      data.claim_details.indirect_tax_type !==
        caseConstants.CASE_TAX_TYPE_NO_INDIRECT_TAX &&
      data.claim_details.indirect_tax_type !==
        caseConstants.CASE_TAX_TYPE_REVERSE_CHARGE
    ) {
      setIsTaxable(true);
      if (
        !isNull(data.claim_details.indirect_tax_percentage) &&
        data.claim_details.indirect_tax_percentage >= 0
      ) {
        setValidTax(true);
      } else setValidTax(false);
    } else setIsTaxable(false);
  }, [
    data.claim_details.indirect_tax_type,
    data.claim_details.indirect_tax_percentage,
  ]);

  /**
   * Converts tax type enum (VAT, SALES_TAX, GST) to user-friendly label for display.
   *  Too many if-else statements for simple mapping
   * Shorter & cleaner, Easy to add more tax types
   */
  useEffect(() => {
    if (
      data.charge_details &&
      data.charge_details.indirect_tax_type === "SALES_TAX"
    ) {
      setSelectedTax("Sales Tax");
    } else if (
      data.charge_details &&
      data.charge_details.indirect_tax_type === "VAT"
    ) {
      setSelectedTax("VAT");
    } else if (
      data.charge_details &&
      data.charge_details.indirect_tax_type === "GST"
    ) {
      setSelectedTax("GST");
    } else setSelectedTax("");
  }, [data.charge_details]);

  /**
   * Formats opponent contact info (email + name) into a single display string.
   * @triggers
   * When Opponent contact details change
   *
   * Issues & Improvements:
   * Issue 1: displayedContactData in dependency array (causes extra re-runs)
   * Issue 2: Repetitive code with multiple concatenations
   * - Remove problematic dependency
   * - Shorter & cleaner code
   * - Less if-else nestin
   */
  useEffect(() => {
    if (data.opponent_contact_details) {
      if (
        data.opponent_contact_details.email &&
        data.opponent_contact_details.name
      ) {
        setDisplayedContactData(
          data.opponent_contact_details.email +
            " - " +
            data.opponent_contact_details.name,
        );
      } else if (data.opponent_contact_details.email) {
        setDisplayedContactData(data.opponent_contact_details.email);
      } else if (data.opponent_contact_details.name) {
        setDisplayedContactData(data.opponent_contact_details.name);
      }
    } else {
      setDisplayedContactData("No contact details set");
    }
  }, [data.opponent_contact_details, displayedContactData]); // ← displayedContactData triggers re-run after setDisplayedContactData()

  /**
 * Refetches case data after an email is saved to the activity log.
 * Runs When: Any email/letter is saved to the case activity log, whether it was:
 * - Sent via SMTP (sendEmail)
 * - Sent as letter (sendLetter)
 * - Manually copied & logged (addEmailToLog)
 * Issue 1: No Error Handling for Case Refetch
 * Issue 2: Dependency Array Missing data.id and data.overview (Using `data` as dependency is too broad. Should use `data.id` and `data.overview` instead)
 * Issue 3: Doesn't clear the status after refetch (could trigger again)

 * Improvements:
 * - Error handling for refetch
 * - Proper dependencies (data.id, data.overview)
 * - Clears status to prevent multiple runs
 * - User sees error if refetch fails
 */
  useEffect(() => {
    const responseStatus = caseReducer.status.CASE_SAVE_EMAIL_TO_LOG;
    if (responseStatus && responseStatus === "success") {
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
    }
  }, [caseReducer.status.CASE_SAVE_EMAIL_TO_LOG, dispatch, data]); // ← entire data object

  const handleResetCase = () => {
    data.was_activated
      ? dispatch(caseActions.updateCaseStateActivate(data.id))
      : dispatch(caseActions.updateCaseStateReset(data.id));
  };

  /**
   * Handles reset/activate case completion: refresh global lists, refetch case, close reset popup, or show errors.
   *
   * Used from reset/activate actions (`handleResetCase`, related popups). Uses a short timeout before
   * refetch so backend state can settle. Overlaps partially with the separate “not sent” activate effect.s
   *
   * @triggers
   * When reset/activate status changes or reset popup state relevant to error path.
   *
   * Issue 1: Uses setTimeout 100ms (Unreliable)
   * Issue 2: Status Clearing Not Immediate, Status stays "success" for 100ms, If user clicks reset again, effect runs again (race condition)
   * Issue 3: Error Path Only Clears if Popup is Open
   *
   * Improvements:
   * split into two effects, one for success path, one for error path?
   */
  useEffect(() => {
    if (
      caseReducer.status.CASE_UPDATE_STATE_RESET === "success" ||
      caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "success" ||
      caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "not sent"
    ) {
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      /* Activating/resetting a case creates new activities. Refetch them
      directly from here so the new cards show up */
      dispatch(caseActivityActions.getCaseActivities(data.id));
      setTimeout(() => {
        dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
        setOpenResetCasePopup(false);
        dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_RESET")); // ← Before dispatching Clearing Status, move this out of setTimeout
        dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE")); // ← Before dispatching Clearing Status, move this out of setTimeout
      }, 100); // ← Increased from 100ms to 500ms (more reliable)
    } else if (
      openResetCasePopup &&
      (catchError(caseReducer.status.CASE_UPDATE_STATE_RESET) ||
        catchError(caseReducer.status.CASE_UPDATE_STATE_ACTIVATE))
    ) {
      showSnackbarError(
        catchError(caseReducer.status.CASE_UPDATE_STATE_RESET)
          ? caseReducer.status.CASE_UPDATE_STATE_RESET
          : caseReducer.status.CASE_UPDATE_STATE_ACTIVATE,
      );
      setOpenResetCasePopup(false);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_RESET")); // Error Path Only Clears if Popup is Open
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE")); // Error Path Only Clears if Popup is Open
    }
  }, [
    caseReducer.status.CASE_UPDATE_STATE_RESET,
    caseReducer.status.CASE_UPDATE_STATE_ACTIVATE,
    openResetCasePopup,
    dispatch,
    data.overview,
    data.id,
    showSnackbarError,
  ]);

  /**
   * Refreshes case and tag catalog after user deletes a tag from the case.
   * @triggers
   * When tag delete status changes.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_DELETE_TAG === "success") {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_TAG"));
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
      dispatch(customerActions.getCaseTags());
    } else if (catchError(caseReducer.status.CASE_DELETE_TAG)) {
      showSnackbarError(caseReducer.status.CASE_DELETE_TAG);
      dispatch(caseActions.clearStateMessage("CASE_DELETE_TAG"));
    }
  }, [
    caseReducer.status.CASE_DELETE_TAG,
    dispatch,
    data.id,
    data.overview,
    showSnackbarError,
  ]);

  /**
   * Handles edge case: activation succeeded but initial email failed to send.
   *
   * Used to inform the user (info popup) and refresh data; points them to case activity for the failed send.
   * Only when `!data.overview?.was_activated` to avoid duplicate messaging vs other activate handlers.
   *
   * @triggers
   * When activate status / overview slice changes to this edge case.
   *
   * Improvements:
   * Delays case refetch 500ms to let backend settle before fetching fresh data.
   */
  useEffect(() => {
    const state = caseReducer.status.CASE_UPDATE_STATE_ACTIVATE;
    if (state === "not sent" && !data.overview?.was_activated) {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE"));
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
      handleOpenInfoPopup(
        "Initial email not sent",
        "The case is now activated, but the initial email could not be sent. The reason is shown in the failed email event in the case activity section. Try resending the email manually after having resolved the issue.",
      );
      setOpenActivateCasePopup(false);
    }
  }, [
    caseReducer.status.CASE_UPDATE_STATE_ACTIVATE,
    dispatch,
    data.id,
    data.overview,
  ]);

  const handleDeleteTag = (tagName) => {
    dispatch(caseActions.deleteCaseTag(data.id, tagName));
  };

  const renderTags = () => {
    var retval = [];
    if (data && data.tags && Array.isArray(data.tags) && data.tags.length > 0) {
      for (const item of data.tags) {
        retval.push(
          <Chip
            className={styles.tag_chip}
            key={item}
            variant="outlined"
            title={item}
            label={item}
            onDelete={() => handleDeleteTag(item)}
          ></Chip>,
        );
      }
    }
    return retval;
  };

  return (
    <div>
      <div className={styles.root}>
        <div className={styles.activation_section}>
          <div className={styles.status_container}>
            <div className={styles.status_box}>
              <div className={styles.status_icon}>
                {caseStates[data.state].icon}
              </div>
              <div className={styles.status_name}>
                {caseStates[data.state].name}
              </div>
            </div>
            <div className={styles.status_date}>
              {formatDatetime(data.date_created, "short")}
            </div>
          </div>

          <div className={styles.host_container}>
            <div className={styles.host}>{data.host}</div>
            <div className={styles.source_id}>
              {data.source_case_id} Case ID: {data.case_id}
              {data.state !== "STANDBY" && data.state !== "CANCELLED" && (
                <>
                  <Tooltip
                    title={"View case in settlement portal"}
                    placement="bottom-start"
                  >
                    <Button
                      className={styles.btn_view_case}
                      onClick={() => handleAutoLogin()}
                    >
                      View case{" "}
                      <LaunchOutlined
                        fontSize="small"
                        className={styles.view_case_icon}
                      />
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>

          <div className={styles.btn_container}>
            {data.state === "STANDBY" ? (
              <div className={styles.activate_btns_container}>
                <Tooltip title={"Cancel case"} placement="top-start">
                  <div
                    className={styles.cancel_icon}
                    onClick={() => setOpenCancelCasePopup(true)}
                  >
                    {caseStates["CANCELLED"].icon}
                  </div>
                </Tooltip>
                <Button
                  className={styles.btn_activate}
                  onClick={() => handleCaseActivation()}
                  disabled={!activable}
                >
                  ACTIVATE
                </Button>
              </div>
            ) : data.state === "ACTIVE" ? (
              <Tooltip
                title={
                  data.invoices?.some(
                    (invoice) =>
                      invoice.type ===
                      invoiceTemplateConstants.INVOICE_TYPE_ACTIVE,
                  )
                    ? "This case contains an active invoice and can't be cancelled."
                    : ""
                }
                placement="top"
                arrow
              >
                <span>
                  <Button
                    className={styles.btn_cancel}
                    onClick={() => setOpenCancelCasePopup(true)}
                    disabled={caseActivityReducer.activities.some(
                      (activity) =>
                        activity.type ===
                          caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED &&
                        activity.content?.type === "ACTIVE",
                    )}
                  >
                    CANCEL
                  </Button>
                </span>
              </Tooltip>
            ) : data.state === "PAID" ? (
              <></>
            ) : // <Button className={styles.btn_payment} onClick={()=>setOpenInfoPaymentPopup(true)}>PAYMENT INFO</Button>
            data.state === "VALID_LICENSE" ? (
              <></>
            ) : // <Button className={styles.btn_payment} onClick={()=>setOpenInfoLicensePopup(true)}>LICENSE INFO</Button>
            data.state === "CANCELLED" ? (
              <Button
                className={styles.btn_reset}
                onClick={() => setOpenResetCasePopup(true)}
              >
                {data.was_activated ? "REACTIVATE" : "RESET"}
              </Button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className={styles.details_container}>
          <div className={styles.finding_section}>
            <div className={styles.image_list}>
              {data.findings && data.findings.length > 0 ? (
                <>
                  <div className={styles.no_finding_container_with_image}>
                    {(data.state === "STANDBY" ||
                      data.state === caseConstants.CASE_STATE_ACTIVE) && (
                      <div className={styles.btn_add_finding_with_image}>
                        <Button
                          className={styles.btn_add_finding}
                          onClick={() => setOpenAddFindingPopup(true)}
                        >
                          <b className={styles.plus_sign}>+</b>Add image use
                        </Button>
                      </div>
                    )}
                  </div>
                  <img
                    className={styles.image_item}
                    src={
                      selectedImage &&
                      selectedImage.original_image.original_image_url
                    }
                    alt="original url"
                    key={selectedImage && selectedImage.id}
                  />
                  <div className={styles.image_icons_container}>
                    {data.findings &&
                      data.findings.map((finding) => (
                        <img
                          className={
                            finding === selectedImage && selectedImage
                              ? styles.image_icon_selected
                              : styles.image_icon
                          }
                          src={finding.original_image.original_image_url}
                          alt="original url"
                          key={finding.id}
                          onMouseEnter={() => setSelectedImage(finding)}
                        />
                      ))}
                  </div>
                </>
              ) : (
                <div className={styles.no_finding_container}>
                  <div className={styles.add_finding_container}>
                    <Button
                      className={styles.btn_add_finding}
                      onClick={() => setOpenAddFindingPopup(true)}
                    >
                      <b className={styles.plus_sign}>+</b>Add image use
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div
              className={styles.evidence_container}
              onClick={openEvidancePopup}
            >
              <div className={styles.evidence_text}>Image uses</div>
              <div className={styles.evidence_action}>
                <div className={styles.finding_count}>
                  {data.findings ? data.findings.length : 0} pic(s)
                </div>
                <NextIcon />
              </div>
            </div>
            <div className={styles.tag_section}>
              <div className={styles.tags_container}>{renderTags()}</div>
              <Tooltip title="Add Case Tag" placement="bottom" arrow>
                <AddCircleOutline
                  className={styles.add_tag_icon}
                  onClick={() => setOpenAddCaseTagPopup(true)}
                />
              </Tooltip>
            </div>
          </div>
          <div className={styles.overview_section}>
            {data.state === "STANDBY" && (
              <>
                <div className={styles.case_templage_container}>
                  <button
                    style={{ padding: "4px 4px 4px 4px", fontFamily: "Roboto" }}
                    onClick={() => setTemplatePopupOpen(true)}
                  >
                    AUTO FILL
                  </button>
                </div>
              </>
            )}

            <div
              className={`${styles.contact_container} `}
              onClick={langSelectionCLicked}
            >
              {data.state !== "STANDBY" ? (
                <></>
              ) : validTranslation ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              <div className={styles.title}>Portal Language</div>
              <div
                className={styles.template_container}
                href={"#"}
                rel="noopener noreferrer"
              >
                <div className={styles.template_icon}>
                  <LangIcon />
                </div>

                <div className={styles.template_title}>
                  {(data.translation &&
                    data.translation.language.toUpperCase()) ||
                    (languageReducer.translations &&
                      languageReducer.translations.default_translation_id &&
                      "DEFAULT") ||
                    "No active translation"}
                </div>
              </div>
            </div>

            <hr />

            <div
              className={`${styles.email_flow_container} ${
                data.state !== caseConstants.CASE_STATE_STANDBY &&
                data.state !== caseConstants.CASE_STATE_CANCELLED
                  ? styles.no_hover
                  : ""
              }`}
              onClick={
                data.state === caseConstants.CASE_STATE_STANDBY ||
                data.state === caseConstants.CASE_STATE_CANCELLED
                  ? handleOpenEmailFlowPopup
                  : () => {}
              }
            >
              <div
                className={`${styles.email_flow_left_container} ${
                  data.state !== caseConstants.CASE_STATE_STANDBY &&
                  data.state !== caseConstants.CASE_STATE_CANCELLED
                    ? styles.disabled
                    : ""
                }`}
              >
                <div className={styles.title}>Mail Flow</div>
                <div
                  className={styles.template_container}
                  href={"#"}
                  rel="noopener noreferrer"
                >
                  <div className={styles.template_icon}>
                    <EmailOutlined />
                  </div>

                  <div className={styles.template_title}>
                    {(flow && flow.name) || "No mail flow is set"}
                  </div>
                </div>
              </div>

              {data.state !== "STANDBY" ? (
                <></>
              ) : flow ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              {(data.state === "ACTIVE" ||
                data.state === "VALID_LICENSE" ||
                data.state === "PAYMENT_PROCESSING" ||
                data.state === "PAID" ||
                data.state === "CANCELLED") && (
                <>
                  <Button
                    variant="outlined"
                    className={styles.btn_send_email}
                    onClick={handleOpenMenu}
                    endIcon={<ArrowDropDownIcon />}
                  >
                    Send Mail
                  </Button>
                  <Menu
                    id="simple-menu"
                    anchorEl={openMenu}
                    keepMounted={false}
                    open={Boolean(openMenu)}
                    onClose={handleCloseMenu}
                  >
                    {templates[templates.length - 1]?.id ===
                      emailConstants.NEW_MAIL && (
                      <MenuItem
                        className={styles.template_dropdown_first}
                        onClick={(event) =>
                          handleOpenTemplatePopup(
                            event,
                            templates[templates.length - 1]?.id,
                            templates[templates.length - 1]?.name,
                          )
                        }
                      >
                        {templates[templates.length - 1]?.name}
                      </MenuItem>
                    )}

                    {templates.some(
                      (template) =>
                        template.type === emailConstants.TEMPLATE_TYPE_CUSTOM,
                    ) && (
                      <>
                        <ListSubheader>
                          {emailConstants.TEMPLATE_TYPE_CUSTOM} EMAILS
                        </ListSubheader>
                        {templates
                          .filter(
                            (template) =>
                              template.type ===
                              emailConstants.TEMPLATE_TYPE_CUSTOM,
                          )
                          .map((template, index, filteredTemplates) => (
                            <MenuItem
                              className={styles.template_dropdown}
                              key={index}
                              onClick={(event) =>
                                handleOpenTemplatePopup(
                                  event,
                                  template.id,
                                  template.name,
                                  template.type,
                                )
                              }
                            >
                              {template.name}
                            </MenuItem>
                          ))}
                      </>
                    )}

                    {templates.some(
                      (template) =>
                        template.type === emailConstants.TEMPLATE_TYPE_LETTER,
                    ) && (
                      <>
                        <ListSubheader>LETTERS</ListSubheader>
                        {templates
                          .filter(
                            (template) =>
                              template.type ===
                              emailConstants.TEMPLATE_TYPE_LETTER,
                          )
                          .map((template, index, filteredTemplates) => (
                            <MenuItem
                              className={styles.template_dropdown}
                              key={index}
                              onClick={(event) =>
                                handleOpenTemplatePopup(
                                  event,
                                  template.id,
                                  template.name,
                                  template.type,
                                )
                              }
                            >
                              {template.name}
                            </MenuItem>
                          ))}{" "}
                      </>
                    )}
                  </Menu>
                </>
              )}
              {data.state === caseConstants.CASE_STATE_STANDBY && (
                <>
                  <Button
                    variant="outlined"
                    className={styles.btn_send_email}
                    onClick={handleOpenPreviewMenu}
                    endIcon={<ArrowDropDownIcon />}
                    style={{ width: "45%" }}
                  >
                    Preview mail
                  </Button>
                  <Menu
                    id="simple-menu"
                    anchorEl={openPreviewMenu}
                    keepMounted={false}
                    open={Boolean(openPreviewMenu)}
                    onClose={handleClosePreviewMenu}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {templates?.length > 0 ? (
                      <>
                        {templates.some(
                          (template) =>
                            template.type ===
                              emailConstants.TEMPLATE_TYPE_TRIGGERED &&
                            template.trigger_event ===
                              emailConstants.TRIGGER_EVENT_INITIAL,
                        ) && (
                          <>
                            <ListSubheader>INITIAL EMAIL</ListSubheader>
                            {templates
                              .filter(
                                (template) =>
                                  template.type ===
                                    emailConstants.TEMPLATE_TYPE_TRIGGERED &&
                                  template.trigger_event ===
                                    emailConstants.TRIGGER_EVENT_INITIAL,
                              )
                              .map((template, index) => (
                                <MenuItem
                                  className={styles.template_dropdown}
                                  key={index}
                                  onClick={(event) =>
                                    handleOpenTemplatePopup(
                                      event,
                                      template.id,
                                      template.name,
                                    )
                                  }
                                >
                                  {template.name}
                                </MenuItem>
                              ))}
                          </>
                        )}
                        {templates.some(
                          (template) =>
                            template.type ===
                            emailConstants.TEMPLATE_TYPE_SCHEDULED,
                        ) && (
                          <>
                            <ListSubheader>
                              {emailConstants.TEMPLATE_TYPE_SCHEDULED} EMAILS
                            </ListSubheader>
                            {templates
                              .filter(
                                (template) =>
                                  template.type ===
                                  emailConstants.TEMPLATE_TYPE_SCHEDULED,
                              )
                              .map((template, index) => (
                                <MenuItem
                                  className={styles.template_dropdown}
                                  key={index}
                                  onClick={(event) =>
                                    handleOpenTemplatePopup(
                                      event,
                                      template.id,
                                      template.name,
                                      template.type,
                                    )
                                  }
                                >
                                  {template.name}
                                </MenuItem>
                              ))}
                          </>
                        )}
                        {templates.some(
                          (template) =>
                            template.type ===
                              emailConstants.TEMPLATE_TYPE_TRIGGERED &&
                            template.trigger_event !==
                              emailConstants.TRIGGER_EVENT_INITIAL,
                        ) && (
                          <>
                            <ListSubheader>
                              EVENT TRIGGERED EMAILS
                            </ListSubheader>
                            {templates
                              .filter(
                                (template) =>
                                  template.type ===
                                    emailConstants.TEMPLATE_TYPE_TRIGGERED &&
                                  template.trigger_event !==
                                    emailConstants.TRIGGER_EVENT_INITIAL,
                              )
                              .map((template, index) => (
                                <MenuItem
                                  className={styles.template_dropdown}
                                  key={index}
                                  onClick={(event) =>
                                    handleOpenTemplatePopup(
                                      event,
                                      template.id,
                                      template.name,
                                      template.type,
                                    )
                                  }
                                >
                                  {template.name}
                                </MenuItem>
                              ))}
                          </>
                        )}
                        {templates.some(
                          (template) =>
                            template.type ===
                            emailConstants.TEMPLATE_TYPE_CUSTOM,
                        ) && (
                          <>
                            <ListSubheader>
                              {emailConstants.TEMPLATE_TYPE_CUSTOM} EMAILS
                            </ListSubheader>
                            {templates
                              .filter(
                                (template) =>
                                  template.type ===
                                  emailConstants.TEMPLATE_TYPE_CUSTOM,
                              )
                              .map((template, index) => (
                                <MenuItem
                                  className={styles.template_dropdown}
                                  key={index}
                                  onClick={(event) =>
                                    handleOpenTemplatePopup(
                                      event,
                                      template.id,
                                      template.name,
                                      template.type,
                                    )
                                  }
                                >
                                  {template.name}
                                </MenuItem>
                              ))}
                          </>
                        )}{" "}
                        {templates.some(
                          (template) =>
                            template.type ===
                            emailConstants.TEMPLATE_TYPE_LETTER,
                        ) && (
                          <>
                            <ListSubheader>LETTERS</ListSubheader>
                            {templates
                              .filter(
                                (template) =>
                                  template.type ===
                                  emailConstants.TEMPLATE_TYPE_LETTER,
                              )
                              .map((template, index) => (
                                <MenuItem
                                  className={styles.template_dropdown}
                                  key={index}
                                  onClick={(event) =>
                                    handleOpenTemplatePopup(
                                      event,
                                      template.id,
                                      template.name,
                                      template.type,
                                    )
                                  }
                                >
                                  {template.name}
                                </MenuItem>
                              ))}
                          </>
                        )}{" "}
                      </>
                    ) : (
                      <MenuItem
                        onClick={(event) => event.stopPropagation()}
                        className={styles.template_dropdown}
                      >
                        This mail flow contains no templates to be previewed.
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </div>
            <hr />
            <div
              className={styles.contact_container}
              onClick={() => setOpenContactInfoPopup(true)}
            >
              {data.state !== "STANDBY" ? (
                <></>
              ) : validContact ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              <div className={styles.title}>Website Owner Contact Info</div>

              <div className={styles.contact_info_box}>
                <div className={styles.contact_big_title}>
                  {displayedContactData}
                </div>
              </div>
            </div>
            <hr />
            <div
              className={styles.claim_container}
              onClick={() => setOpenClaimPopup(true)}
            >
              {data.state !== "STANDBY" ? (
                <></>
              ) : validClaim ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              <div className={styles.title}>Licensing Offer</div>
              <div className={styles.claim_content}>
                <div>
                  <div>
                    <div className={styles.claim_info_box}>
                      <div className={styles.claim_small_title_component}>
                        Country
                      </div>
                      <div className={styles.claim_big_title}>
                        {data.state === "VALID_LICENSE"
                          ? "----"
                          : data.country || "----"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={styles.claim_info_box}>
                      <div className={styles.claim_small_title}>
                        Net License Fee
                      </div>
                      <div className={styles.claim_big_title}>
                        {data.state === "VALID_LICENSE" ||
                        (data.charge_details &&
                          !data.charge_details?.total_net_amount_findings)
                          ? "----"
                          : getCurrencySign(data.charge_details?.currency) +
                            " " +
                            data.charge_details?.total_net_amount_findings}
                      </div>
                    </div>
                    <div className={styles.claim_info_box}>
                      <div className={styles.claim_small_title}>
                        Gross Incl.{" "}
                        {data.charge_details &&
                          data.charge_details.indirect_tax_type !==
                            caseConstants.CASE_TAX_TYPE_NO_INDIRECT_TAX &&
                          data.charge_details.indirect_tax_type !==
                            caseConstants.CASE_TAX_TYPE_REVERSE_CHARGE &&
                          selectedTax + " & "}{" "}
                        Fees
                      </div>
                      <div className={styles.claim_big_title}>
                        {data.state === "VALID_LICENSE" ? (
                          "----"
                        ) : (
                          <span>
                            <span>
                              {data.charge_details &&
                              data.charge_details
                                .final_amount_gross_no_settlement
                                ? getCurrencySign(data.claim_details.currency) +
                                  " "
                                : ""}
                            </span>
                            <span>
                              {data.charge_details &&
                              data.charge_details
                                .final_amount_gross_no_settlement
                                ? data.charge_details
                                    .final_amount_gross_no_settlement
                                : "Not Set"}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className={styles.claim_info_box}>
                    <div className={styles.claim_small_title}>Discount</div>
                    <div className={styles.claim_big_title}>
                      {data.state === "VALID_LICENSE"
                        ? "----"
                        : data.charge_details &&
                            data.charge_details.settlement_offer &&
                            Number(
                              data.charge_details.settlement_offer
                                .settlement_percentage,
                            ).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })
                          ? "% " +
                            Number(
                              data.charge_details.settlement_offer
                                .settlement_percentage,
                            ).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })
                          : "Not Set"}
                    </div>
                  </div>
                  <div className={styles.claim_info_box}>
                    <div className={styles.claim_small_title}>
                      Discount Deadline
                    </div>
                    <div className={styles.claim_big_title}>
                      {data.state === "VALID_LICENSE"
                        ? "----"
                        : data.charge_details &&
                            data.charge_details.settlement_offer
                          ? formatDatetime(
                              data.charge_details.settlement_offer
                                .settlement_date,
                              "short",
                            )
                          : "Not Set"}
                    </div>
                  </div>
                  <div className={styles.claim_info_box}>
                    <div className={styles.claim_small_title}>
                      Discounted Total
                    </div>
                    <div className={styles.claim_big_title}>
                      {data.state === "VALID_LICENSE" ? (
                        "----"
                      ) : (
                        <span>
                          <span>
                            {data.charge_details &&
                            data.charge_details.final_amount_gross_settlement
                              ? getCurrencySign(data.claim_details.currency) +
                                " "
                              : ""}
                          </span>
                          <span>
                            {data.charge_details &&
                            data.charge_details.final_amount_gross_settlement
                              ? data.charge_details
                                  .final_amount_gross_settlement
                              : "Not Set"}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <hr />
            {data.state === "ACTIVE" && (
              <>
                <div className={styles.deadline_container}>
                  <div className={styles.deadline_title_container}>
                    <div className={styles.title}>Reminder</div>
                    <div>
                      <Button
                        variant="outlined"
                        className={styles.btn_add_deadline}
                        onClick={() => {
                          setReminderData(caseReducer.case[data.id]);
                          setOpenReminderPopup(true);
                        }}
                      >
                        {data.reminder_name ? "Edit Reminder" : "Set Reminder"}
                      </Button>
                    </div>
                  </div>
                  <div className={styles.deadline_content}>
                    <div className={styles.deadline_info_box}>
                      <div className={styles.deadline_small_title_component}>
                        Title
                      </div>
                      <div
                        className={styles.deadline_big_title}
                        title={data.reminder_name}
                      >
                        {data.reminder_name
                          ? shortnString(data.reminder_name, 17)
                          : "----"}
                      </div>
                    </div>

                    <div className={styles.deadline_info_box}>
                      <div className={styles.deadline_small_title_component}>
                        Date
                      </div>
                      <div className={styles.deadline_big_title}>
                        {data.reminder_date
                          ? formatDatetime(data.reminder_date, "short")
                          : "----"}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {data.state === "STANDBY" && (
        <>
          <ActivateCasePopup
            open={openActivateCasePopup}
            close={() => setOpenActivateCasePopup(false)}
            data={data}
            caseId={data.id}
            stripeConnected={isConnected}
          />
          <StripeInfoPopup
            open={openStripeInfoPopup}
            close={() => setOpenStripeInfoPopup(false)}
            stripeUrl={stripeUrl}
          />
        </>
      )}
      <InfoPopup
        open={openInfoPopup}
        close={() => setOpenInfoPopup(false)}
        title={InfoPopupTitle}
      >
        <p>{infoPopupText}</p>
      </InfoPopup>
      <CancelCasePopup
        open={openCancelCasePopup}
        close={() => setOpenCancelCasePopup(false)}
        data={data}
        caseId={data.id}
        overviewData={data.overview}
      />
      <TemplatePopup
        open={templatePopupOpen}
        close={() => setTemplatePopupOpen(false)}
        data={data}
        caseId={data.id}
        overviewData={data.overview}
      />
      <AreYouSurePopup
        open={openResetCasePopup}
        close={() => setOpenResetCasePopup(false)}
        onClick={handleResetCase}
      >
        Are you sure you want to {data.was_activated ? "reactivate" : "reset"}{" "}
        this case?
      </AreYouSurePopup>
      <InfoPaymentPopup
        open={openInfoPaymentPopup}
        close={() => setOpenInfoPaymentPopup(false)}
        data={data}
        caseId={data.id}
      />
      <InfoLicensePopup
        open={openInfoLicensePopup}
        close={() => setOpenInfoLicensePopup(false)}
        data={data}
        caseId={data.id}
      />
      <ClaimPopup
        open={openClaimPopup}
        close={() => setOpenClaimPopup(false)}
        data={data}
        caseId={data.id}
      />
      <ContactInfoPopup
        open={openContactInfoPopup}
        close={() => setOpenContactInfoPopup(false)}
        data={data}
        opponentData={data.opponent_contact_details}
        caseId={data.id}
      />
      <FindingsPopup
        open={openFindingsPopup}
        close={() => setOpenFindingsPopup(false)}
        data={data}
        overviewData={data.overview}
        imprintData={data.website_contact_details}
        findingsData={data.findings}
        caseId={data.id}
      />
      <AddFindingPopup
        open={openAddFindingPopup}
        close={() => setOpenAddFindingPopup(false)}
        caseId={data.id}
        allData={data}
      />
      <SelectEmailFlowPopup
        open={openEmailFlowPopup}
        close={() => setOpenEmailFlowPopup(false)}
        data={data}
        caseId={data.id}
        defaultFlow={flow}
      />
      <SelectTranslationPopup
        open={openTranslationPopup}
        close={() => setOpenTranslationPopup(false)}
        data={data}
        caseId={data.id}
      />
      {openReminderPopup && (
        <ReminderPopup
          open={openReminderPopup}
          close={() => setOpenReminderPopup(false)}
          data={reminderData}
          caseId={data.id}
          doClose={() => setOpenReminderPopup(false)}
        />
      )}
      <RenderPopup
        title={`${
          emailData.name === emailConstants.NEW_MAIL
            ? "New mail"
            : "Mail preview: " + emailData.name
        }`}
        open={openRenderPopup}
        close={() => setOpenRenderPopup(false)}
        data={emailData}
        caseId={data.id}
        emailSetup={emailSetup}
      />
      <AddCaseTagPopup
        open={openAddCaseTagPopup}
        close={() => setOpenAddCaseTagPopup(false)}
        tags={data.tags}
        caseData={data}
      />
      <AppSnackbar {...snackbarProps} />
    </div>
  );
}

export default OverviewSection;
